// Client-side image compression. Big phone photos (10+ MB) shrink to <1 MB
// before upload — avoids server rejections and speeds up the admin flow.

const DEFAULTS = {
  maxWidth: 2400,
  maxHeight: 2400,
  quality: 0.85,
  skipUnderKB: 400, // don't recompress small files
};

export async function compressImage(file, opts = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  // GIFs and WEBPs animated shouldn't be recompressed via canvas — pass through.
  if (file.type === "image/gif" || file.type === "image/webp") return file;

  const { maxWidth, maxHeight, quality, skipUnderKB } = { ...DEFAULTS, ...opts };
  if (file.size < skipUnderKB * 1024) return file;

  const bmp = await loadBitmap(file);
  const { width: srcW, height: srcH } = bmp;
  const scale = Math.min(maxWidth / srcW, maxHeight / srcH, 1);
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bmp, 0, 0, w, h);
  if (bmp.close) bmp.close();

  // JPEG for photos (great compression), keep PNG only if source is PNG
  // with likely transparency — safer to force JPEG for phone photos.
  const outputType = file.type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise((res) => canvas.toBlob(res, outputType, quality));
  if (!blob) return file;

  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
  const ext = outputType === "image/jpeg" ? ".jpg" : ".png";
  const compressed = new File([blob], baseName + ext, { type: outputType, lastModified: Date.now() });

  // Rare edge case: compression made it bigger — keep the original.
  return compressed.size < file.size ? compressed : file;
}

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file); } catch { /* fallback below */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
